/**
 * @file   文物易绘-详情页（首页第 2 屏空白处点击 / 菜单「智慧保护-文物易绘」进入）
 * @create 2026/9/16
 * @description 参考原型「瑞鹿云-门户网站原型4/collaboration.html + capabilities-refinements.css」实现：
 *              深色首屏（文物线图大图 + 文案 + 免费试用）+
 *              核心能力（四张概览卡片，点击进入对应详情区块）+
 *              线图生成（对照图 + 支持多种文物类型 + 绘制风格）+
 *              在线编辑 / 成果导出 / 归档管理 三个详情区块 + 产品定价 + 返回顶部
 *              免费试用：未登录跳转本站原有注册页，已登录免登录直接进入文物易绘；
 *              页头复用门户站点；页脚改为原型的商务咨询 / 联合发布收尾区（仅本页生效，不影响其它页面）
 */
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { IntroHeader } from '../header';
import { localDS } from '../../../storage';
import archaeologyPaper from '../../../assets/home/archaeology-paper.png';
import workflowComparison from '../../../assets/home/workflow-comparison2.png';
import editingInterface from '../../../assets/home/editing-interface.jpg';
import exportLineDrawing from '../../../assets/home/export-line-drawing.png';
import archiveProjectList from '../../../assets/home/archive-project-list.png';
import wechatConsultQr from '../../../assets/home/wechat-consultation-qr.png';
import shuwenLogo from '../../../assets/home/shuwen-tech-white.png';
import heritageLogo from '../../../assets/home/zhejiang-ai-cultural-heritage-logo.jpg';
import './ArchaeologyLineDrawing.scss';

/** 图标路径（取自原型 SVG sprite，部分图标为多段路径） */
const ICON_PATHS = {
    arrow: ['M4 12h15m-6-6 6 6-6 6'],
    lineGenerate: ['M13 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-8', 'm3 16 5-5 4 4 3-3 6 6M18 2v6m-3-3h6', 'M7 7a1 1 0 1 1 2 0 1 1 0 0 1-2 0Z'],
    lineEdit: ['m12 3 9 9-7 7-8-1-1-8 7-7Z', 'm5 19 4-4M6 18l-3 3M14 5l5 5', 'M9 13a2 2 0 1 1 4 0 2 2 0 0 1-4 0Z'],
    resultExport: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z', 'M14 2v6h6M12 11v7m-3-3 3 3 3-3'],
    projectArchive: ['M21 8v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4l2 3h8a2 2 0 0 1 2 2Z', 'M8 11h8M8 15h8M8 18h5'],
    spark: ['m12 3 2.4 6.6L21 12l-6.6 2.4L12 21l-2.4-6.6L3 12l6.6-2.4L12 3Z'],
    pen: ['m4 20 4.5-1 10-10-3.5-3.5-10 10L4 20Zm9-12 3.5 3.5M15 5.5 17 3.5a1.4 1.4 0 0 1 2 0L20.5 5a1.4 1.4 0 0 1 0 2l-2 2'],
    search: ['M16.5 10.5a6 6 0 1 1-12 0 6 6 0 0 1 12 0Z', 'm15.5 15.5 5 5'],
    vessel: ['M8 4h8M9 4c0 2-2 3-2 7 0 5 2 8 5 9 3-1 5-4 5-9 0-4-2-5-2-7M7 10h10M8 15h8'],
    bronze: ['M5 6h14M7 6v5c0 3 2 5 5 5s5-2 5-5V6M8 16l-1 5m9-5 1 5M5 9h2m10 0h2M10 3h4'],
    stone: ['m5 18-2-6 5-8 8 1 5 7-4 7-8 1-4-2Z', 'm8 4 3 5 10 3M3 12l8-3-2 11'],
    jade: ['m12 3 8 6-8 12L4 9l8-6Z', 'M15 10a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z', 'm4 9 5 1m6 0 5-1'],
    bone: ['M8.5 8.5 15.5 15.5M6.8 10.2a3 3 0 1 1-3-4.7 3 3 0 1 1 4.7-3l9 9a3 3 0 1 1 3 4.7 3 3 0 1 1-4.7 3l-9-9Z'],
    layers: ['m3 8 9-5 9 5-9 5-9-5Zm0 4 9 5 9-5M3 16l9 5 9-5'],
    ruler: ['m4 16 12-12 4 4L8 20 4 16Z', 'm13 7 2 2m-5 1 2 2m-5 1 2 2'],
    smooth: ['M3 17C5 17 5 7 9 7s3 10 7 10 3-10 5-10'],
    breakLine: ['m3 19 6-6m6-4 6-6M7 8l3 3m3 3 3 3'],
    styleAdjust: ['M4 6h16M4 12h16M4 18h16M8 3v6m8 0v6m-6 0v6'],
    fileImage: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8ZM14 2v6h6', 'm7 18 3-4 2 2 2-3 3 5H7Z', 'M8.75 11a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z'],
    fileVector: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8ZM14 2v6h6M8 17c0-6 8-6 8 0M8 12h8', 'M6.5 17h3v3h-3ZM14.5 17h3v3h-3ZM10.5 10.5h3v3h-3Z'],
    close: ['m6 6 12 12M6 18 18 6']
};

/**
 * @description 线性图标
 * @param {String} type 图标类型
 * @param {String} className 类名（默认 icon，卡片内用 feature-icon 等）
 */
export const Icon = ({ type, className = 'icon' }) => (
    <svg className={className} viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.55' strokeLinecap='round' strokeLinejoin='round'>
        {(ICON_PATHS[type] || []).map((d, index) => <path key={index} d={d} />)}
    </svg>
);

/** 核心能力（原型 capabilities 区块：四张概览卡片，点击进入对应详情区块） */
const CAPABILITIES = [
    {
        icon: 'lineGenerate', panelClass: 'compose-panel', title: '线图生成', anchor: 'workflow',
        desc: '基于文物影像智能生成线图，支持多种类型文物与绘图风格选择'
    },
    {
        icon: 'lineEdit', panelClass: 'edit-panel', title: '在线编辑', anchor: 'editing',
        desc: '叠加原始文物影像，提供便捷、灵活的文物线图矢量化编辑优化'
    },
    {
        icon: 'resultExport', panelClass: 'export-panel', title: '成果导出', anchor: 'result-export',
        desc: '支持多分辨率图片和矢量线图文件快速导出'
    },
    {
        icon: 'projectArchive', panelClass: 'archive-panel', title: '归档管理', anchor: 'archive',
        desc: '提供文物线图按项目进行自定义归档管理'
    }
];

/** 绘制风格（原型 features 区块） */
const DRAWING_STYLES = [
    { icon: 'pen', title: '标准', desc: '完整保留文物轮廓与主要纹饰，展示文物整体结构' },
    { icon: 'spark', title: '高清细节', desc: '最大化保留文物铭文、纹饰和细小边缘，还原文物细节' },
    { icon: 'vessel', title: '纯轮廓', desc: '仅保留文物外形轮廓，忽略内部碎线，突出文物轮廓形体' }
];

/** 文物类型（原型 generation-types 区块） */
const ARTIFACTS = [
    { icon: 'layers', title: '通用' },
    { icon: 'vessel', title: '陶器' },
    { icon: 'bronze', title: '铜器' },
    { icon: 'stone', title: '石器' },
    { icon: 'jade', title: '玉器' },
    { icon: 'bone', title: '骨器' }
];

/** 在线编辑功能清单（原型 editing-function-list 区块） */
const EDITING_FUNCTIONS = [
    { icon: 'pen', title: '临摹描绘' },
    { icon: 'lineEdit', title: '线条编辑' },
    { icon: 'styleAdjust', title: '样式调整' },
    { icon: 'smooth', title: '平滑' },
    { icon: 'breakLine', title: '打断' },
    { icon: 'ruler', title: '尺寸标定' }
];

/** 成果导出格式（原型 export 区块） */
const EXPORT_FORMATS = [
    { title: '线图图片', desc: '标准、高清线图图片快速导出' },
    { title: '矢量文件', desc: 'SVG 格式文件导出，便于第三方应用' }
];

/** 归档管理功能清单（原型 archive-function-list 区块） */
const ARCHIVE_FUNCTIONS = [
    { icon: 'projectArchive', title: '分类管理' },
    { icon: 'search', title: '快速检索' },
    { icon: 'resultExport', title: '批量导出' }
];

/** 个人版 / 标准版 / 高级版共用的完整权益清单 */
const FULL_FEATURES = ['线图管理', '平面图生成', '平剖图生成', '在线编辑', '标准 / 高清 PNG 导出', '矢量图导出'];

/** 产品定价（原型 05 / PLANS & PRICING 区块） */
const PLANS = [
    {
        name: '试用版', cardClass: 'trial-plan', amount: '免费', action: '免费试用', actionType: 'register',
        note: '购买普通加油包可升级个人版',
        facts: [{ label: '额度', value: '20 次', suffix: '/单图' }, { label: '协作账号', value: '1 个账号' }, { label: '数据存储', value: '30 天' }],
        features: [
            { text: '线图管理', on: true },
            { text: '平面图生成', on: true },
            { text: '标准 PNG 导出', on: true },
            { text: '平剖图生成', on: false },
            { text: '在线编辑', on: false },
            { text: '高清 PNG 导出', on: false },
            { text: '矢量图导出', on: false }
        ]
    },
    {
        name: '个人版', cardClass: 'personal-plan', currency: '¥', amount: '300', unit: '元/年', action: '购买个人版', actionType: 'register',
        note: '可购买普通加油包增加额度',
        facts: [{ label: '额度', value: '100 次', suffix: '/单图' }, { label: '协作账号', value: '1 个账号' }, { label: '数据存储', value: '1 年' }],
        features: FULL_FEATURES.map(text => ({ text, on: true }))
    },
    {
        name: '机构标准版', cardClass: 'standard-plan featured-plan', currency: '¥', amount: '2,800', unit: '元/年', action: '线下咨询购买', actionType: 'consult',
        note: '可购买高级加油包增加额度',
        facts: [{ label: '额度', value: '1,500 次', suffix: '/单图' }, { label: '协作账号', value: '1 主账号 + 4 子账号' }, { label: '数据存储', value: '1 年' }],
        features: FULL_FEATURES.map(text => ({ text, on: true }))
    },
    {
        name: '机构高级版', cardClass: 'advanced-plan', currency: '¥', amount: '5,800', unit: '元/年', action: '线下咨询购买', actionType: 'consult',
        note: '可购买高级加油包增加额度',
        facts: [{ label: '额度', value: '3,500 次', suffix: '/单图' }, { label: '协作账号', value: '1 主账号 + 9 子账号' }, { label: '数据存储', value: '1 年' }],
        features: FULL_FEATURES.map(text => ({ text, on: true }))
    }
];

/** 加油包（原型 addon-grid 区块） */
const ADDONS = [
    { name: '普通加油包', cardClass: 'standard-addon', times: '100', scope: '仅限于试用版和个人版', price: '300' },
    { name: '高级加油包', cardClass: 'premium-addon', times: '300', scope: '仅限于机构标准版和机构高级版', price: '600' }
];

/** 咨询电话：页脚产品咨询区与线下咨询弹框统一使用 */
const CONSULT_PHONE = '18162722753';
const CONSULT_PHONE_TEXT = '181-6272-2753';
/** 页脚产品咨询区按原型用空格分隔展示 */
const CONSULT_PHONE_SPACED_TEXT = '181 6272 2753';

/** 锚点定位的额外下移量（px）：产品定价区块落点再往下 100px */
const ANCHOR_OFFSETS = { pricing: 100 };

/**
 * @description 文物易绘详情页组件
 */
export const ArchaeologyLineDrawing = () => {
    const { hash } = useLocation();
    /** 当前咨询的套餐 / 加油包名称，为空表示不展示咨询弹框 */
    const [consultPlan, setConsultPlan] = useState(null);

    /**
     * 平滑滚动到锚点（首屏下滑提示、返回顶部、查看定价等复用）
     * @param {String} anchorId 锚点 id（不含 #）
     * @param {String} behavior 滚动方式：smooth 平滑 / auto 立即
     */
    const scrollToAnchor = (anchorId, behavior = 'smooth') => {
        const anchor = document.getElementById(anchorId);
        if (!anchor) {
            return;
        }
        const offset = ANCHOR_OFFSETS[anchorId] || 0;
        const top = anchor.getBoundingClientRect().top + window.pageYOffset + offset;
        console.log('[ArchaeologyLineDrawing] 滚动到锚点: ' + JSON.stringify({ anchorId: anchorId, top: top }));
        window.scrollTo({ top: top, behavior: behavior });
    };

    /**
     * 核心能力卡片点击：平滑滚动到对应详情区块
     * 说明：保留 href 语义（便于复制链接/无障碍），由 scrollToAnchor 统一处理定位与偏移
     */
    const onAnchorClick = (event, anchorId) => {
        event.preventDefault();
        scrollToAnchor(anchorId);
    };

    /**
     * 从首页「查看定价」等入口带 #pricing 进入时，自动定位到产品定价区块
     * 说明：跨页进入时页面资源（大图/字体）可能尚未加载完成，锚点位置随后会变化，
     *      故在页面加载完成后再校正一次，保证落点准确
     */
    useEffect(() => {
        if (!hash) {
            return;
        }
        const anchorId = hash.slice(1);
        scrollToAnchor(anchorId);
        if (document.readyState === 'complete') {
            return;
        }
        let cancelled = false;
        const correctAnchor = () => {
            if (!cancelled) {
                console.log('[ArchaeologyLineDrawing] 页面加载完成，校正锚点定位: ' + JSON.stringify({ anchorId: anchorId }));
                scrollToAnchor(anchorId);
            }
        };
        window.addEventListener('load', correctAnchor, { once: true });
        // 兜底：load 事件已过或图片未触发 load 时，延迟再校正一次
        const correctTimer = setTimeout(correctAnchor, 800);
        return () => {
            cancelled = true;
            window.removeEventListener('load', correctAnchor);
            clearTimeout(correctTimer);
        };
    }, [hash]);

    /**
     * 免费试用点击：已登录时免登录直接进入文物易绘（与首页文物易绘屏、产品列表口径一致），
     * 统一携带 authorizationc（门户不再使用 authorizationu 跳转）；
     * 未登录时保持原逻辑，走 Link 默认跳转注册页
     */
    const onTrialClick = (event) => {
        const saasToken = localDS.get('saasToken');
        if (!saasToken) {
            console.log('[ArchaeologyLineDrawing] 免费试用-未登录，跳转注册页: ' + JSON.stringify({ to: '/register?from=red' }));
            return;
        }
        event.preventDefault();
        const targetLink = window.RED_LOGIN + `?authorizationc=${saasToken}`;
        console.log('[ArchaeologyLineDrawing] 免费试用-已登录，跳转文物易绘: ' + JSON.stringify({ targetLink: targetLink }));
        window.location.href = targetLink;
    };

    return (<div className='archaeology-page'>
        <IntroHeader />
        {/* 首屏：文物线图大图 + 文案 */}
        <section className='hero' id='top'>
            <div className='hero-visual' aria-hidden='true'>
                <img src={archaeologyPaper} alt='' />
            </div>
            <div className='hero-copy'>
                <h1><i aria-hidden='true' />文物易绘</h1>
                <p>
                    依托 AI 图像生成技术，由文物影像自动生成符合《田野考古制图》行业标准的专业文物线图。<br />
                    一站式完成文物平面图、平剖图生成与在线优化编辑，支持出版级文物线图成果导出及归档管理，<br />
                    大幅降低文物绘图工作量，是文博考古工作者的专业智能绘图助手。
                </p>
                <div className='hero-actions'>
                    <Link className='primary-action' to='/register?from=red' onClick={onTrialClick}>
                        免费试用
                        <Icon type='arrow' />
                    </Link>
                    <button type='button' className='secondary-action' onClick={() => scrollToAnchor('pricing')}>
                        查看定价
                    </button>
                </div>
            </div>
            <button type='button' className='scroll-cue' aria-label='向下查看核心能力' onClick={() => scrollToAnchor('capabilities')}>
                <span />
            </button>
        </section>

        {/* 01 核心能力：四张概览卡片 */}
        <section className='content-section capabilities' id='capabilities'>
            <div className='section-heading'>
                <h2>核心能力</h2>
                <span>一站式完成文物线图生成、编辑、导出与管理。</span>
            </div>
            <div className='capability-grid'>
                {CAPABILITIES.map(item => (
                    <article className={`capability-panel ${item.panelClass}`} key={item.title}>
                        <a className='capability-body' href={`#${item.anchor}`} aria-label={`了解${item.title}`} onClick={event => onAnchorClick(event, item.anchor)}>
                            <div className='capability-meta'><Icon type={item.icon} className='feature-icon' /></div>
                            <h3>{item.title}</h3>
                            <p>{item.desc}</p>
                        </a>
                    </article>
                ))}
            </div>
        </section>

        {/* 02 线图生成：影像/线图对照 + 文物类型与绘制风格 */}
        <section className='capability-detail generation-detail' id='workflow'>
            <div className='section-heading'>
                <h2>线图生成</h2>
            </div>
            <figure className='workflow-figure'>
                <img src={workflowComparison} alt='青铜器文物影像与生成的文物线图对照' />
                <figcaption>基于文物影像智能生成线图，支持多种类型文物与绘图风格选择</figcaption>
            </figure>
            <div className='generation-supplements'>
                <section className='generation-types' id='artifacts'>
                    <h3>文物类型</h3>
                    <div className='generation-type-list'>
                        {ARTIFACTS.map(item => (
                            <div key={item.title}>
                                <Icon type={item.icon} />
                                <h4>{item.title}</h4>
                            </div>
                        ))}
                    </div>
                </section>
                <section className='generation-styles' id='features'>
                    <h3>绘制风格</h3>
                    <dl>
                        {DRAWING_STYLES.map(item => (
                            <div key={item.title}>
                                <dt><Icon type={item.icon} /><span>{item.title}</span></dt>
                                <dd>{item.desc}</dd>
                            </div>
                        ))}
                    </dl>
                </section>
            </div>
        </section>

        {/* 03 在线编辑：编辑界面截图 + 编辑能力说明 */}
        <section className='capability-detail editing-detail' id='editing'>
            <div className='section-heading'>
                <h2>在线编辑</h2>
            </div>
            <div className='feature-showcase editing-showcase'>
                <div className='editing-summary-layout'>
                    <figure className='editing-focus-figure'>
                        <img src={editingInterface} alt='在线编辑界面，展示线图编辑工具栏与编辑属性面板' />
                    </figure>
                    <ul className='editing-function-list' aria-label='在线编辑主要功能'>
                        {EDITING_FUNCTIONS.map(item => (
                            <li key={item.title}><Icon type={item.icon} />{item.title}</li>
                        ))}
                    </ul>
                </div>
                <p className='showcase-caption'>叠加原始文物影像，提供便捷、灵活的文物线图矢量化编辑优化</p>
            </div>
        </section>

        {/* 04 成果导出：线图预览 + 三种导出格式 */}
        <section className='capability-detail export-detail' id='result-export'>
            <div className='section-heading'>
                <h2>成果导出</h2>
            </div>
            <figure className='feature-showcase export-showcase'>
                <div className='export-layout'>
                    <div className='export-diagram' role='img' aria-label='成果导出示意：一份文物线图分别输出 PNG 图片和 SVG 矢量文件'>
                        <div className='export-source-sheet'>
                            <img src={exportLineDrawing} alt='文物线图' />
                        </div>
                        <svg className='export-branches' viewBox='0 0 50 200' preserveAspectRatio='none' aria-hidden='true'>
                            <path d='M0 100H20V42H48M20 100V158H48' />
                            <path d='m42 37 6 5-6 5m0 106 6 5-6 5' />
                        </svg>
                        <div className='export-output-files'>
                            <div><Icon type='fileImage' /><span>PNG</span></div>
                            <div><Icon type='fileVector' /><span>SVG</span></div>
                        </div>
                    </div>
                    <ul className='export-format-list' aria-label='成果导出格式'>
                        {EXPORT_FORMATS.map(item => (
                            <li className='export-format-option' key={item.title}>
                                <div className='format-copy'>
                                    <div className='format-title'>
                                        <h3>{item.title}</h3>
                                    </div>
                                    <p>{item.desc}</p>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
                <figcaption>支持多分辨率图片和矢量线图文件快速导出</figcaption>
            </figure>
        </section>

        {/* 05 归档管理：项目档案示例 + 说明 */}
        <section className='capability-detail archive-detail' id='archive'>
            <div className='section-heading'>
                <h2>归档管理</h2>
            </div>
            <div className='feature-showcase archive-showcase'>
                <div className='archive-summary-layout'>
                    <figure className='archive-project-scene'>
                        <img src={archiveProjectList} alt='归档管理界面：按项目归档文物影像与线图，支持分类检索与批量导出' />
                    </figure>
                    <ul className='archive-function-list' aria-label='归档管理主要功能'>
                        {ARCHIVE_FUNCTIONS.map(item => (
                            <li key={item.title}><Icon type={item.icon} />{item.title}</li>
                        ))}
                    </ul>
                </div>
                <p className='showcase-caption'>提供文物线图按项目进行自定义归档管理</p>
            </div>
        </section>

        {/* 05 产品定价 */}
        <section className='pricing-section' id='pricing'>
            <div className='section-heading'>
                <h2>产品定价</h2>
            </div>
            <div className='pricing-grid'>
                {PLANS.map(plan => (
                    <article className={`plan-card ${plan.cardClass}`} key={plan.name}>
                        <div className='plan-head'>
                            <h3>{plan.name}</h3>
                            <p>
                                <strong>{plan.currency ? <em>{plan.currency}</em> : null}{plan.amount}</strong>
                                {plan.unit ? <small>{plan.unit}</small> : null}
                            </p>
                            {plan.note ? <span className='plan-note'>{plan.note}</span> : null}
                        </div>
                        <dl className='plan-facts'>
                            {plan.facts.map(fact => (
                                <div key={fact.label}>
                                    <dt>{fact.label}</dt>
                                    <dd>{fact.value}{fact.suffix ? <small>{fact.suffix}</small> : null}</dd>
                                </div>
                            ))}
                        </dl>
                        <div className='plan-divider'><span>包含权益</span></div>
                        <ul className='plan-features'>
                            {plan.features.map(feature => (
                                <li className={feature.on ? 'is-on' : 'is-off'} key={feature.text}>{feature.text}</li>
                            ))}
                        </ul>
                        {plan.actionType === 'register'
                            ? <Link className='plan-action individual-plan-action' to='/register?from=red' onClick={onTrialClick}>
                                {plan.action}
                                <Icon type='arrow' />
                            </Link>
                            : <button type='button' className='plan-action consultation-plan-action' onClick={() => setConsultPlan(plan.name)}>
                                {plan.action}
                                <Icon type='arrow' />
                            </button>}
                    </article>
                ))}
            </div>
            <div className='addon-grid'>
                {ADDONS.map(addon => (
                    <aside className={`quota-addon ${addon.cardClass}`} key={addon.name} aria-label={addon.name}>
                        <div className='addon-ticket-copy'>
                            <span>{addon.name}</span>
                            <h3><strong>{addon.times}</strong> 次/单图</h3>
                            <p>{addon.scope}</p>
                        </div>
                        <div className='addon-price'>
                            <strong><em>¥</em>{addon.price}元</strong>
                        </div>
                        <Link className='plan-action' to='/register?from=red' onClick={onTrialClick}>
                            立即购买
                            <Icon type='arrow' />
                        </Link>
                    </aside>
                ))}
            </div>
        </section>

        {/* 页脚：产品咨询 + 联合发布（对齐原型 collaboration.html 的 final-cta，仅本页使用） */}
        <footer className='final-cta' aria-label='产品咨询与联合发布'>
            <div className='final-contact'>
                <figure className='wechat-contact'>
                    <img src={wechatConsultQr} alt='产品咨询微信二维码' />
                </figure>
                <div className='contact-copy'>
                    <small>产品咨询</small>
                    <a href={`tel:${CONSULT_PHONE}`}>{CONSULT_PHONE_SPACED_TEXT}</a>
                </div>
            </div>
            <div className='partner-block'>
                <div className='final-partners' aria-label='数文科技与浙江省人工智能与文化遗产国际科技合作基地'>
                    <img className='shuwen-logo' src={shuwenLogo} alt='数文科技' />
                    <span aria-hidden='true' />
                    <img className='hcu-logo' src={heritageLogo} alt='浙江省人工智能与文化遗产国际科技合作基地' />
                </div>
                <small>联合发布</small>
            </div>
            <button type='button' className='back-top' onClick={() => scrollToAnchor('top')}>
                返回顶部
                <Icon type='arrow' />
            </button>
        </footer>

        {/* 线下咨询弹框（参考原型 auth-dialog） */}
        {consultPlan && createPortal(
            <div className='consult-dialog-mask' onClick={() => setConsultPlan(null)}>
                <div className='consult-dialog' role='dialog' aria-modal='true' aria-labelledby='consult-dialog-title' onClick={event => event.stopPropagation()}>
                    <button type='button' className='dialog-close' aria-label='关闭' onClick={() => setConsultPlan(null)}>
                        <Icon type='close' />
                    </button>
                    <p className='dialog-eyebrow'>瑞鹿云 · 文物易绘</p>
                    <h2 id='consult-dialog-title'>{consultPlan}</h2>
                    <span className='dialog-message'>欢迎联系瑞鹿云，了解团队套餐权益与开通方式。</span>
                    <a className='dialog-phone' href={`tel:${CONSULT_PHONE}`}>
                        <small>咨询电话</small>
                        <strong>{CONSULT_PHONE_TEXT}</strong>
                    </a>
                    <button type='button' className='dialog-confirm' onClick={() => setConsultPlan(null)}>我已了解</button>
                </div>
            </div>,
            document.body
        )}
    </div>
    )
}